import { zohoAuthService } from './zohoAuth';

interface ForumPost {
  id?: string;
  title: string;
  content: string;
  tags?: string[];
  attachments?: File[];
  projectId: string;
  portalId: string;
  projectName?: string;
  flag?: 'internal' | 'external';
}

interface ForumPostResponse {
  success: boolean;
  postId?: string;
  error?: string;
}

class ZohoProjectsApiService {
  private baseUrl: string;

  constructor() {
    // Utiliser le backend proxy pour éviter les problèmes CORS
    this.baseUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';
  }

  // Récupérer tous les projets d'un portail selon la documentation Zoho Projects API
  async getProjects(token: string, portalId: string): Promise<Array<{ id: string; name: string; description: string }>> {
    try {
      const allProjects: Array<{ id: string; name: string; description: string }> = [];
      let hasMoreProjects = true;
      let page = 1;
      const perPage = 100; // Limite recommandée par l'API Zoho Projects
      
      while (hasMoreProjects) {
        const url = `${this.baseUrl}/api/zoho/projects/api/v3/portal/${portalId}/projects?page=${page}&per_page=${perPage}`;

          
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });


        if (response.ok) {
          const data = await response.json();

          
          // Extraire les projets selon la structure de l'API Zoho Projects
          let projects: Array<{ id: string; name: string; description: string }> = [];
          
          if (data.projects && Array.isArray(data.projects)) {
            projects = data.projects;
          } else if (data.data && Array.isArray(data.data)) {
            projects = data.data;
          } else if (Array.isArray(data)) {
            projects = data;
          } else {
            break;
          }
          
          // Ajouter les projets à la liste totale
          allProjects.push(...projects);
          
          // Vérifier s'il y a plus de pages selon la documentation Zoho Projects
          if (data.page_info) {
            hasMoreProjects = data.page_info.has_next_page === true;
          } else if (projects.length < perPage) {
            // Si on a moins de projets que la limite, c'est probablement la dernière page
            hasMoreProjects = false;
          } else {
            // Par défaut, continuer si on a le nombre maximum de projets
            hasMoreProjects = projects.length === perPage;
          }
          
          page++;
          
          // Pause entre les requêtes pour respecter les rate limits
          if (hasMoreProjects) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } else {
          break;
        }
      }
      
      return allProjects;
      
    } catch (error) {
      return [];
    }
  }

  // Récupérer les catégories de forum d'un projet
  async getProjectCategories(token: string, portalId: string, projectId: string): Promise<any[]> {
    try {
      const url = `${this.baseUrl}/api/zoho/projects/api/v3/portal/${portalId}/projects/${projectId}/categories`;
        
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Essayer différents formats possibles
        if (data.categories && Array.isArray(data.categories)) {
          return data.categories;
        } else if (data.data && Array.isArray(data.data)) {
          return data.data;
        } else if (Array.isArray(data)) {
          return data;
        } else {
          return [];
        }
      } else {
        return [];
      }
    } catch (error) {
      return [];
    }
  }

  // Récupérer les forums d'un projet
  async getProjectForums(token: string, portalId: string, projectId: string): Promise<any[]> {
    try {
      const url = `${this.baseUrl}/api/zoho/projects/api/v3/portal/${portalId}/projects/${projectId}/forums`;
        
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Essayer différents formats possibles
        if (data.forums && Array.isArray(data.forums)) {
          return data.forums;
        } else if (data.data && Array.isArray(data.data)) {
          return data.data;
        } else if (Array.isArray(data)) {
          return data;
        } else {
          return [];
        }
      } else {
        return [];
      }
    } catch (error) {
      return [];
    }
  }

  // Récupérer les utilisateurs clients d'un projet
  async getProjectUsers(token: string, portalId: string, projectId: string): Promise<Array<{ id: string; name: string; email: string; role: string }>> {
    try {
      // Utiliser l'endpoint officiel pour récupérer les utilisateurs clients d'un projet
      const endpoint = `/api/v3/portal/${portalId}/projects/${projectId}/clients/users`;
      
      // Paramètres de requête selon la documentation
      const params = new URLSearchParams({
        view_type: '1', // 1 = Active users only
        per_page: '100' // Maximum d'utilisateurs par page
      });
      
      const url = `${this.baseUrl}/api/zoho/projects${endpoint}?${params.toString()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Fonction helper pour extraire une valeur string de manière sûre
        const extractString = (obj: any, ...keys: string[]): string => {
          for (const key of keys) {
            const value = obj?.[key];
            if (value !== null && value !== undefined) {
              if (typeof value === 'string') {
                return value;
              } else if (typeof value === 'object') {
                const nestedValue = value.name || value.value || value.display || JSON.stringify(value);
                if (typeof nestedValue === 'string') {
                  return nestedValue;
                }
              }
            }
          }
          return '';
        };

        // Essayer différents formats possibles selon la documentation
        let users: Array<{ id: string; name: string; email: string; role: string }> = [];
        
        if (data.clients && Array.isArray(data.clients)) {
          // Structure: clients[].users[]
          for (const client of data.clients) {
            if (client.users && Array.isArray(client.users)) {
              const clientUsers = client.users.map((user: any) => ({
                id: String(extractString(user, 'id', 'user_id', 'userid', 'zpuid')),
                name: extractString(user, 'display_name', 'full_name', 'name', 'displayname'),
                email: extractString(user, 'email', 'email_id', 'emailid'),
                role: extractString(user, 'role', 'user_role', 'userrole', 'type', 'user_type') || 'client'
              }));
              
              users.push(...clientUsers);
            }
          }
        } else if (data.users && Array.isArray(data.users)) {
          // Structure: users[] direct
          users = data.users.map((user: any) => ({
            id: String(extractString(user, 'id', 'user_id', 'userid', 'zpuid')),
            name: extractString(user, 'display_name', 'full_name', 'name', 'displayname'),
            email: extractString(user, 'email', 'email_id', 'emailid'),
            role: extractString(user, 'role', 'user_role', 'userrole', 'type', 'user_type') || 'client'
          }));
        } else if (data.data && Array.isArray(data.data)) {
          // Structure: data[]
          users = data.data.map((user: any) => ({
            id: String(extractString(user, 'id', 'user_id', 'userid', 'zpuid')),
            name: extractString(user, 'display_name', 'full_name', 'name', 'displayname'),
            email: extractString(user, 'email', 'email_id', 'emailid'),
            role: extractString(user, 'role', 'user_role', 'userrole', 'type', 'user_type') || 'client'
          }));
        } else if (Array.isArray(data)) {
          // Structure: array direct
          users = data.map((user: any) => ({
            id: String(extractString(user, 'id', 'user_id', 'userid', 'zpuid')),
            name: extractString(user, 'display_name', 'full_name', 'name', 'displayname'),
            email: extractString(user, 'email', 'email_id', 'emailid'),
            role: extractString(user, 'role', 'user_role', 'userrole', 'type', 'user_type') || 'client'
          }));
        } else {
          return [];
        }
        
        // Filtrer les utilisateurs avec des données valides
        users = users.filter(user => user.id && user.name);
        
        return users;
        
      } else {
        return [];
      }
      
    } catch (error) {
      return [];
    }
  }

  // Créer un post de type forum dans un projet
  async createForumPost(token: string, postData: ForumPost, notifyUsers: boolean = true, userZpuids: string[] = []): Promise<ForumPostResponse> {
    try {
      // Récupérer les catégories du projet
      const categories = await this.getProjectCategories(token, postData.portalId, postData.projectId);
      
      // Logique de fallback pour les catégories :
      // 1. Chercher une catégorie avec le même nom que le projet
      // 2. Si pas trouvée, prendre la première catégorie
      // 3. Si aucune catégorie, utiliser 1 par défaut
      let selectedCategory = null;
      
      if (categories.length > 0) {
        // Essayer de trouver une catégorie avec le même nom que le projet
        const projectNameCategory = categories.find(cat => 
          cat.name && cat.name.toLowerCase() === postData.projectName?.toLowerCase()
        );
        
        if (projectNameCategory) {
          selectedCategory = projectNameCategory;
        } else {
          selectedCategory = categories[0];
        }
      }

      // Préparer les données du forum selon la documentation Zoho Projects API v3
      const forumPayload: any = {
        title: postData.title,
        content: postData.content,
        category_id: selectedCategory ? selectedCategory.id : 1, // Utiliser la catégorie sélectionnée ou 1 par défaut
        tags: postData.tags || [],
        flag: postData.flag || 'internal', // Utiliser le flag choisi par l'utilisateur ou 'internal' par défaut
        type: 'normal', // Type normal (pas une question)
        is_sticky: false,
        is_announcement: false, // Toujours false pour éviter les problèmes avec les forums externes
      };

      // Ajouter les ZPUID des utilisateurs à notifier si spécifiés
      if (notifyUsers && userZpuids.length > 0) {
        forumPayload.notify_users = userZpuids;
      }

      // Créer directement un post de type forum
      const url = `${this.baseUrl}/api/zoho/projects/api/v3/portal/${postData.portalId}/projects/${postData.projectId}/forums`;
        
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(forumPayload)
      });

      if (response.ok) {
        // Essayer de récupérer l'ID du post depuis la réponse
        let postId = null;
        
        try {
          const data = await response.json();
          postId = data.post_id || data.id || data.data?.id || data.post?.id || data.forum_id || data.forum?.id;
        } catch (jsonError) {
          // Essayer de récupérer l'ID depuis les headers de réponse
          const locationHeader = response.headers.get('location');
          if (locationHeader) {
            const match = locationHeader.match(/\/(\d+)$/);
            if (match) {
              postId = match[1];
            }
          }
        }
        
        return {
          success: true,
          postId: postId || 'unknown'
        };
      } else {
        const errorText = await response.text();
        
        // Essayer de parser l'erreur JSON si possible
        let errorMessage = `Erreur ${response.status}: ${errorText}`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // Garder le message d'erreur brut si ce n'est pas du JSON
        }
        
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }

  // Uploader un fichier pour un projet
  async uploadProjectAttachment(token: string, portalId: string, projectId: string, file: File): Promise<{ id: string; url: string } | null> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const url = `${this.baseUrl}/api/zoho/projects/api/v3/portal/${portalId}/projects/${projectId}/attachments`;
        
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        
        return {
          id: data.attachment_id || data.id || data.data?.id,
          url: data.url || data.data?.url
        };
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  // Envoyer un message à plusieurs projets
  async sendMessageToProjects(messageData: {
    title: string;
    content: string;
    tags: string[];
    attachments: File[];
    projectIds: string[];
    portalId: string;
    projects: Array<{ id: string; name: string }>;
    notifyUsers?: boolean;
    projectUsers?: {[projectId: string]: Array<{id: string; name: string; email: string; role: string}>};
    flag?: 'internal' | 'external';
  }): Promise<{ success: boolean; results: Array<{ projectId: string; postId?: string; error?: string }> }> {
    const results: Array<{ projectId: string; postId?: string; error?: string }> = [];
    const token = await zohoAuthService.getValidToken();

    if (!token) {
      return {
        success: false,
        results: messageData.projectIds.map(projectId => ({
          projectId,
          error: 'Token d\'authentification non disponible'
        }))
      };
    }

    // Upload des pièces jointes d'abord (si supporté)
    const attachmentIds: string[] = [];
    if (messageData.attachments.length > 0) {
      for (const file of messageData.attachments) {
        try {
          const attachment = await this.uploadProjectAttachment(token, messageData.portalId, messageData.projectIds[0], file);
          if (attachment) {
            attachmentIds.push(attachment.id);
          }
        } catch (error) {
          // Continuer sans ce fichier
        }
      }
    }

    // Envoyer le message à chaque projet
    for (const projectId of messageData.projectIds) {
      try {
        // Trouver les informations du projet pour récupérer le nom
        const project = messageData.projects.find(p => p.id === projectId);
        
        // Récupérer les ZPUID des utilisateurs clients de ce projet
        const projectClientUsers = messageData.projectUsers?.[projectId] || [];
        const userZpuids = projectClientUsers.map(user => user.id);
        
        const postData: ForumPost = {
          title: messageData.title,
          content: messageData.content,
          tags: messageData.tags,
          projectId,
          portalId: messageData.portalId,
          projectName: project?.name,
          flag: messageData.flag
        };

        const result = await this.createForumPost(token, postData, messageData.notifyUsers, userZpuids);

        if (result.success) {
          results.push({
            projectId,
            postId: result.postId
          });
        } else {
          results.push({
            projectId,
            error: result.error || 'Erreur inconnue'
          });
        }

        // Attendre un peu entre chaque envoi pour respecter les rate limits
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        results.push({
          projectId,
          error: error instanceof Error ? error.message : 'Erreur inconnue'
        });
      }
    }

    const success = results.some(r => r.postId);
    return { success, results };
  }
}

export const zohoProjectsApiService = new ZohoProjectsApiService(); 